// components/SidebarMenu.tsx
import React, { useState, useEffect } from 'react';
import { Menu, Layout, Tooltip } from 'antd';
import {
  DashboardOutlined,
  AppstoreAddOutlined,
  UsergroupAddOutlined,
  BuildOutlined,
  ProfileOutlined,
  TeamOutlined,
  SolutionOutlined,
  UserOutlined,
  DollarOutlined,
  EditOutlined,
  FileTextOutlined,
  TrophyOutlined,
  LogoutOutlined,
  FileProtectOutlined,
  LockOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  MinusCircleOutlined,
  AuditOutlined,
  MinusSquareOutlined,
  PlusCircleOutlined,
  CalculatorOutlined,
  ClockCircleOutlined,
  BankOutlined,
  AppstoreOutlined,
  StarOutlined,
  PauseCircleOutlined,
  ShareAltOutlined,
  VideoCameraOutlined,
  DesktopOutlined,
  CalendarOutlined,
  HomeOutlined,
  MinusOutlined,
  BarChartOutlined,
  PartitionOutlined,
  AlertOutlined,
  RiseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
const logo = '/resourceai.svg';
import { getOrganizationById } from '../services/AdminServices/OrganizationManagementServices/organizationManagementService';
import { getUserProfileImageUrl } from '../utils/fileUrlUtils';
import { useAppContext } from '../contexts/AppContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { GlobalOutlined } from '@ant-design/icons';
import { FeatureCode } from '../constants/featureCodes';
import FeatureMenuItem from './FeatureMenuItem';
import LockedSubMenu from './LockedSubMenu';
import { useNavAccess } from '../hooks/useNavAccess';

/** A single locked leaf item gated by a *module* (not a feature) — e.g. "My Appraisals" under PERFORMANCE. */
const LockedModuleMenuItem: React.FC<{
  label: string;
  menuKey: string;
  icon?: React.ReactNode;
  moduleCode: string;
  onLockedClick: (moduleCode: string) => void;
}> = ({ label, menuKey, icon, moduleCode, onLockedClick }) => (
  <Menu.Item
    key={menuKey}
    title={label}
    icon={icon ? <span style={{ opacity: 0.45 }}>{icon}</span> : undefined}
    onClick={() => onLockedClick(moduleCode)}
    style={{ opacity: 0.5, cursor: 'pointer' }}
  >
    <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
      <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
      </span>
    </Tooltip>
  </Menu.Item>
);

const { Sider } = Layout;
const { SubMenu, ItemGroup } = Menu;

// ── Compute which SubMenu keys should be open for a given path ────────────────
// Called both as lazy useState initializer AND inside the navigation useEffect.
function computeOpenKeysFromPath(path: string, role?: string): string[] {
  const p = path.toLowerCase();
  const r = (role || '').toString().toLowerCase().trim();
  const keys: string[] = [];
  const add = (k: string) => { if (!keys.includes(k)) keys.push(k); };

  const isAdmin = r === 'admin';
  const isAccountant = r === 'accountant';
  const isHR = r === 'hr';

  // Attendance hub is a top-level item — no submenu to expand (except for HR).
  if (p === '/attendance' && !isHR) return keys;

  if (p.includes('/hr-payroll/attendance') || p.includes('/hr-payroll/leave-requests') ||
    p.includes('/hr-payroll/overtime-submission') || p.includes('/hr-payroll/wfh-requests') ||
    p.includes('/hr-payroll/attendance-regularization')) add('attendance');
  if (p.includes('/hr-payroll/generate-payslip') || p.includes('/hr-payroll/tax-documents') || p.includes('/hr-payroll/my-loans')) add('payroll');
  if ((p.includes('/employee/appraisals') && !p.includes('/employee/appraisals/pending/manager')) || p.includes('/employee/self-evaluation') ||
    p.includes('/kpis/scorecard') || p.includes('/recognition-engagement/kudos-wall') ||
    p.includes('/recognition-engagement/pulse-surveys')) add('employee-recognition');
  if (p.includes('/hr-request') || p.includes('/hr-documents')) add('employee-hr-requests');
  if (p.includes('/self-service') || p.includes('/employee/assets-management')) add('self-service');
  if (p.includes('/document-center')) add('document-center');
  if (p.includes('/recognition-engagement')) add('employee-recognition');
  if (p.includes('/kpi-dashboard') || p.includes('/kpi-rules') || p.includes('/kpi-slabs') ||
    p.includes('/kpi-entries') || p.includes('/kpi-achievements') || p.includes('/kpi-assignments') ||
    p.includes('/kpi-scorecard') || p.includes('/bonus-management')) add('kpi-management');

  // Admin Payroll & Finance
  if (isAdmin) {
    if (p.includes('/payroll-management') || p.includes('/payroll-runs') || p.includes('/payslips-management') ||
      p.includes('/payslip-templates') || p.includes('/payroll-bonus-management') || p.includes('/bonus-types') ||
      p.includes('/deduction-management') || p.includes('/payroll-deductions-management') ||
      p.includes('/tax-management') || p.includes('/tax-rules') || p.includes('/loans') ||
      p.includes('/salary-revisions') || p.includes('/merit-matrix') || p.includes('/employee-allowances-management') ||
      p.includes('/employees-management') || p.includes('/resignations') || p.includes('/final-settlement')) {
      add('admin-payroll-finance');
    }
  } else {
    // Other roles: Accountant
    if (isAccountant) {
      if (p.includes('/payroll-management') || p.includes('/payslips-management') || p.includes('/payslip-templates')) add('payroll');
      if (p.includes('/payroll-bonus-management') || p.includes('/bonus-types')) add('bonus');
      if (p.includes('/deduction-management') || p.includes('/payroll-deductions-management')) add('deduction');
      if (p.includes('/tax-management') || p.includes('/tax-rules') || p.includes('/payroll-tax-management')) add('tax');
      if (p.includes('/salary-revisions') || p.includes('/merit-matrix')) add('salary-revision-management');
    }
  }

  if (p.includes('/managed-hr/leave-requests') || p.includes('/managed-hr/overtime-requests') || p.includes('/managed-hr/wfh')) add('leave-attendance');
  if (p.includes('/users') || p.includes('/employees') || p.includes('/attendance-management') || p.includes('/attendance-analytics') || (p.includes('/warnings') && !p.includes('/employee/warnings') && !p.includes('/hr/warnings')) || (p.includes('/warning-rules') && !p.includes('/hr/warning-rules')) || (p.includes('/warning-types') && !p.includes('/hr/warning-types'))) add('user-management');
  if (p.includes('/leaves-types') || p.includes('/holidays')) add('leaves-and-holidays');
  if (p.includes('/attendance-rules-management') || p.includes('/final-settlement-rules-management') ||
    p.includes('/insurance-management') || p.includes('/provident-fund') ||
    p.includes('/overtime-rates') || (p.includes('/pulse-surveys') && !p.includes('/recognition-engagement/pulse-surveys')) ||
    p.includes('/self-evaluations')) add('rules-group');

  if (p.includes('/issue-tickets-management') || p.includes('/procurement-management') ||
    p.includes('/attendance-regularization')) {
    add('other-group');
  }

  if (p.includes('/organization-management') || p.includes('/location-management') ||
    p.includes('/department-management') || p.includes('/designation-management') ||
    p.includes('/shift-management') || p.includes('/smtp-settings') || p.includes('/smtp/callback') ||
    p.includes('/email-providers')) add('organizations-management');
  if ((p.includes('/appraisals') && !p.includes('/hr/appraisals') && !p.includes('/employee/appraisals')) || p.includes('/appraisals-cycle') || p.includes('/appraisals-templates')) add('appraisal-management');
  if (p.includes('/hr-payroll/attendance-regularization')) add('hr-payroll');
  if (p.includes('/candidates')) add('candidates');
  if (p.includes('/hr/attendance-regularization')) { /* top-level HR item, no submenu to open */ }
  if (p.includes('/employee-documents') || p.includes('/hr-documents-management') || (p.includes('/assets-management') && !p.includes('/employee/assets-management'))) add('employee-documents');
  if (p.includes('/hr/requests') || p.includes('/hr/documents') ||
    p.includes('/hr/employees') || (p.includes('/attendance') && !p.includes('/attendance-rules-management') && !p.includes('/attendance-regularization') && !p.includes('/attendance-requests')) ||
    p.includes('/hr/team-resignations')) add('people-operations-group');
  if (p.includes('/hr/salary-structure') || p.includes('/hr/issue-tickets') || p.includes('/hr/warnings') || p.includes('/hr/warning-rules') || p.includes('/hr/warning-types') ||
    p.includes('/hr/loan-types') || p.includes('/hr/loans')) add('hr-management-group');
  if (p.includes('/hr/appraisals') || p.includes('/hr/appraisals-cycle') ||
    p.includes('/hr/appraisals-templates') || p.includes('/hr/salary-revisions') ||
    p.includes('/hr/merit-matrix')) add('hr-appraisal-revision-group');
  if (p.includes('/accountant/loans') && !isAdmin) add('loan-management');
  if (p.includes('/permissions') || p.includes('/permission-sets') || p.includes('/assign-permissions') || p.includes('/roles')) add('permissions-management');
  if (p.includes('/leave-requests') || p.includes('/overtime-requests') || p.includes('/wfh') || p.includes('/employee/appraisals/pending/manager')) add('leave-attendance-managed');

  return keys;
}

interface SidebarMenuProps {
  collapsed: boolean;
  darkMode: boolean;
  onCollapse: (collapsed: boolean) => void;
  userRole?: string;
  isMobile?: boolean;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  collapsed,
  darkMode,
  onCollapse,
  userRole,
  isMobile = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeRole } = useSelector((state: RootState) => state.auth);
  const { hasModule, currentPlan, appReady } = useAppContext();
  const { canAccess } = useNavAccess();
  const { openUpgradeModal } = useUpgradeModal();

  const getLocalRole = () => {
    try {
      const ud = localStorage.getItem('userDetails');
      if (ud) return (JSON.parse(ud).role || '').toString().trim().toLowerCase();
    } catch { /**/ }
    return '';
  };

  const effectiveRole = (activeRole || userRole || getLocalRole()).toString().trim().toLowerCase();

  const isAdmin = effectiveRole === 'admin';
  const isRecruiter = effectiveRole === 'recruiter';
  const isCandidate = effectiveRole === 'candidate';
  const isEmployee = effectiveRole === 'employee';
  const isAccountant = effectiveRole === 'accountant';
  const isHR = effectiveRole === 'hr';
  const isPlatform = effectiveRole === 'platform';

  // HRMS-13: the Roles & Permissions module (Manage Permissions / Permission Sets /
  // Assign Permissions / Roles) is not fully developed yet, so its whole sidebar group
  // is hidden from the Tenant Admin nav for now. Routes, components and backend APIs are
  // intentionally left untouched — flip this flag to true to restore the nav group once
  // role creation is ready.
  const SHOW_ROLES_PERMISSIONS = false;

  // Lazy initializer — compute correct open keys from the CURRENT path immediately
  // so submenus are open on the very first render (no close-then-open flash).
  const [openKeys, setOpenKeys] = React.useState<string[]>(() =>
    computeOpenKeysFromPath(window.location.pathname, effectiveRole)
  );
  // Seed from a cached brand snapshot so a page refresh paints the org's own
  // logo/name immediately instead of flashing the bundled default for a frame.
  const [orgData, setOrgData] = useState<{ name: string; logoUrl: string } | null>(() => {
    try {
      const cached = localStorage.getItem('orgBrand');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const menuScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollLocked = React.useRef<boolean>(false);

  // Synchronous scroll listener to block scrollIntoView and save user scrolling
  React.useEffect(() => {
    const el = menuScrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (scrollLocked.current) {
        // Restore saved position to block auto-scrolling
        try {
          const saved = sessionStorage.getItem('sidebarScrollTop');
          if (saved) el.scrollTop = parseInt(saved, 10);
        } catch { /**/ }
      } else {
        // Save current scroll position
        try {
          sessionStorage.setItem('sidebarScrollTop', el.scrollTop.toString());
        } catch { /**/ }
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position on navigation
  React.useLayoutEffect(() => {
    const el = menuScrollRef.current;
    if (!el) return;
    try {
      const saved = sessionStorage.getItem('sidebarScrollTop');
      if (saved) {
        const scrollPos = parseInt(saved, 10);
        if (!isNaN(scrollPos)) {
          scrollLocked.current = true;
          el.scrollTop = scrollPos;
          // Keep locked briefly to block any subsequent paint-induced scrollEvents
          const id = setTimeout(() => {
            scrollLocked.current = false;
          }, 150);
          return () => clearTimeout(id);
        }
      }
    } catch { /**/ }
  }, [location.pathname]);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        if (userDetails.organizationId) {
          const response = await getOrganizationById(userDetails.organizationId);
          // Handle both response structures: response.data or response directly
          const orgInfo = response.data || response;
          const brand = { name: orgInfo.name || '', logoUrl: getUserProfileImageUrl(orgInfo.logoUrl) || '' };
          setOrgData(brand);
          // Cache for the next refresh so the real logo paints on first frame.
          try { localStorage.setItem('orgBrand', JSON.stringify(brand)); } catch { /* ignore quota */ }
        }
      } catch (err) {
        console.error('Failed to fetch organization:', err);
      }
    };
    fetchOrg();
  }, []);

  const getIsManager = () => {
    try {
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        const parsed = JSON.parse(userDetails);
        return parsed.isManager || false;
      }
      return false;
    } catch (error) {
      console.error('Error getting manager status:', error);
      return false;
    }
  };
  const isManager = getIsManager();

  // Helper: returns a lock icon suffix when module is not licensed
  const lockSuffix = (moduleCode: string) =>
    !hasModule(moduleCode)
      ? <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4, verticalAlign: 'middle' }} />
      : null;


  // Root-level SubMenu keys — only one of these may be open at a time (accordion).
  // Child SubMenus (e.g. permissions-management inside user-management) are NOT in
  // this set, so they can stay open independently of the accordion logic.
  const rootSubMenuKeys = React.useMemo(() => new Set([
    'organizations-management',
    'user-management',
    'kpi-management',
    'leaves-and-holidays',
    'employee-documents',
    'salary-revision-management',
    'loan-management',
    'appraisal-management',
    'payroll-management',
    'self-service',
    'leave-attendance',
    'leave-attendance-managed',
    'candidates',
    'document-center',
    'payroll',
    'bonus',
    'deduction',
    'tax',
    'rules-group',
    'other-group',
    'hr-management-group',
    'hr-appraisal-revision-group',
    'people-operations-group',
    'attendance',
    'employee-recognition',
    'employee-hr-requests',
  ]), []);

  // When path changes (navigation), recompute which submenus should be open.
  // Accordion: only the LAST root-level key from the path wins — siblings stay closed.
  React.useEffect(() => {
    const pathKeys = computeOpenKeysFromPath(location.pathname, effectiveRole);
    setOpenKeys(prev => {
      // Start with non-root keys that were already open (nested SubMenus).
      const nonRoot = prev.filter(k => !rootSubMenuKeys.has(k));
      // From the path, pick only the last root-level key (accordion).
      const pathRootKeys = pathKeys.filter(k => rootSubMenuKeys.has(k));
      const activeRootKey = pathRootKeys.length > 0 ? pathRootKeys[pathRootKeys.length - 1] : null;
      // Also keep non-root path keys.
      const pathNonRoot = pathKeys.filter(k => !rootSubMenuKeys.has(k));
      const merged = [...nonRoot];
      pathNonRoot.forEach(k => { if (!merged.includes(k)) merged.push(k); });
      if (activeRootKey && !merged.includes(activeRootKey)) merged.push(activeRootKey);
      return merged;
    });
  }, [location.pathname, effectiveRole]);

  const handleOpenChange = (keys: string[]) => {
    // Save scroll position before Ant Design re-renders the menu
    if (menuScrollRef.current) {
      try {
        sessionStorage.setItem('sidebarScrollTop', menuScrollRef.current.scrollTop.toString());
      } catch { /**/ }
    }

    // Accordion: find which root-level key was just opened (present in
    // `keys` but absent from the previous `openKeys`).
    const latestRootKey = keys.find(
      k => rootSubMenuKeys.has(k) && !openKeys.includes(k)
    );

    if (latestRootKey) {
      // Keep only the newly-opened root key; drop all other root keys.
      // Non-root keys (nested SubMenus) are preserved.
      setOpenKeys(keys.filter(k => !rootSubMenuKeys.has(k) || k === latestRootKey));
    } else {
      // A submenu was closed — just pass through.
      setOpenKeys(keys);
    }
  };

  const getSelectedKey = (): string => {
    const path = location.pathname.toLowerCase();

    // Consolidated Attendance hub (single top-level item across roles)
    if (path === '/attendance') return 'attendance-hub';

    // General
    if (path.includes('/candidate/dashboard')) return 'candidate-dashboard';
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/pending-approvals')) return 'pending-approvals';

    if (path.includes('/hr/feed')) return 'hr-feed';
    if (path.includes('/hr/documents')) return 'hr-documents';
    if (path.includes('/hr/requests')) return 'hr-requests';
    if (path.includes('/hr/employees')) return 'hr-employees';
    if (path.includes('/hr/leave-requests')) return 'hr-leave-requests';
    if (path.includes('/hr/overtime-requests')) return 'hr-overtime-requests';
    if (path.includes('/hr/wfh-requests')) return 'hr-wfh-requests';
    if (path.includes('/hr/team-resignations')) return 'hr-team-resignations';
    if (path.includes('/hr/salary-structure')) return 'hr-salary-structure';
    if (path.includes('/hr/bonus-management')) return 'hr-bonus-management';
    if (path.includes('/bonus-types')) return isAdmin ? 'admin-bonus-types' : 'bonus-types';
    if (path.includes('/hr/deduction-management')) return 'hr-deduction-management';
    if (path.includes('/hr/attendance-analytics')) return 'hr-attendance-analytics';
    if (path.includes('/hr/attendance')) return 'hr-attendance';
    if (path.includes('/hr/leave-types')) return 'hr-leave-types';
    if (path.includes('/hr/loan-types')) return 'hr-loan-types';
    if (path.includes('/hr/loans')) return 'hr-loans';
    if (path.includes('/accountant/loans')) return isAdmin ? 'admin-loans' : 'accountant-loans';
    if (path.includes('/hr-payroll/my-loans')) return 'my-loans';
    if (path.includes('/hr/appraisals/pending/hr')) return 'hr-pending-appraisals';
    if (path.includes('/hr/appraisals-cycle')) return 'hr-appraisals-cycle';
    if (path.includes('/hr/appraisals-templates')) return 'hr-appraisals-templates';
    if (path.includes('/hr/appraisals')) return 'hr-appraisals';

    if (path.includes('/jobs/available')) return 'available-jobs';
    if (path.includes('/jobs/applied')) return 'applied-jobs';
    if (path.includes('/candidate/profile')) return 'profile';

    if (path.includes('/feed')) return 'feed';

    if (path === '/v1/leave-requests') return 'all-leave-requests';
    if (path === '/v1/overtime-requests') return 'all-overtime-requests';
    if (path === '/v1/wfh') return 'all-wfh-requests';

    // Recruitment
    if (path.includes('/candidates/status/pending')) return 'candidates-pending';
    if (path.includes('/candidates/user/')) return 'candidates-recruiter';
    if (path.includes('/candidates/status/shortlisted')) return 'candidates-shortlisted';
    if (path.includes('/candidates/status/onhold')) return 'candidates-onhold';
    if (path.includes('/candidates')) return 'all-candidates';
    if (path.includes('/jobs')) return 'jobs';
    if (path.includes('/projects')) return 'projects';
    if (path.includes('/candidate-assessment')) return 'candidate-assessment';

    // ADD THIS LINE FOR WORKFLOW MANAGEMENT
    if (path.includes('/workflow')) return 'workflow-management';
    if (path.includes('/teams-meetings')) return 'teams-meetings';

    // Onboarding / Offboarding / Offer (recruiter) — check '-templates' first so
    // the base path doesn't shadow it.
    if (path.includes('/onboarding-templates')) return 'rec-onboarding-templates';
    if (path.includes('/onboarding')) return 'rec-onboarding';
    if (path.includes('/offer-templates')) return 'rec-offer-templates';
    if (path.includes('/offboarding-templates')) return 'rec-offboarding-templates';
    if (path.includes('/offboarding')) return 'rec-offboarding';

    if (path.includes('/hr/appraisals/pending/manager')) return 'manager-pending-appraisals';
    if (path.includes('/hr/appraisals/pending/hr')) return 'hr-pending-appraisals';

    // Managed HR & Payroll
    if (path.includes('/payslip-templates')) return isAdmin ? 'admin-payslip-templates' : 'payslip-templates';
    if (path.includes('/payslips-management')) return isAdmin ? 'admin-payslips-management' : 'payslips-management';
    if (path.includes('/tax-management')) return isAdmin ? 'admin-tax-management' : 'tax-management';
    if (path.includes('/managed-hr/leave-requests')) return 'leave-requests';
    if (path.includes('/managed-hr/overtime-requests')) return 'overtime-requests';
    if (path.includes('/managed-hr/wfh')) return 'work-from-home-approval';
    if (path.includes('/bonus-management')) return 'bonus-management';
    if (path.includes('/deduction-management')) return isAdmin ? 'admin-deduction-management' : 'deduction-management';
    if (path.includes('/employee-allowances-management')) return 'employee-allowances-management';
    if (path.includes('/payroll-management')) return isAdmin ? 'admin-payroll-management' : 'payroll-management';
    if (path.includes('/payroll-runs')) return isAdmin ? 'admin-payroll-runs' : 'payroll-runs';
    if (path.includes('/tax-rules')) return isAdmin ? 'admin-tax-rules' : 'tax-rules';
    if (path.includes('/salary-structure')) return 'salary-structure';
    if (path.includes('/payroll-bonus-management')) return isAdmin ? 'admin-payroll-bonus-management' : 'payroll-bonus-management';
    if (path.includes('/payroll-deductions-management')) return isAdmin ? 'admin-payroll-deductions-management' : 'payroll-deductions-management';
    if (path.includes('/payroll-tax-management')) return isAdmin ? 'admin-payroll-tax-management' : 'payroll-tax-management';
    if (path.includes('/employees-management')) return 'employees-management';
    if (path.includes('/resignations')) return 'approval-resignation';
    if (path.includes('/final-settlement-rules-management')) return 'final-settlement-rules-management';
    if (path.includes('/final-settlement')) return 'final-settlement';
    if (path.includes('/reimbursement-management')) return 'reimbursement-management';
    if (path.includes('/provident-fund')) return 'provident-fund';

    // Attendance
    if (path.includes('/hr-payroll/attendance')) return 'emp-employee-attendance';
    if (path.includes('/attendance-rules-management')) return 'attendance-rules-management';
    if (path.includes('/attendance-analytics')) return 'admin-attendance-analytics';
    if (path.includes('/attendance-management')) return 'employee-attendance';
    if (path.includes('/attendance') && !path.includes('/attendance-regularization') && !path.includes('/attendance-requests')) return 'attendance';

    // User Management
    if (path.includes('/users')) return 'all-users';
    if (path.includes('/employees')) return 'all-employees';

    // Candidate
    if (path.includes('/jobs/available')) return 'available-jobs';
    if (path.includes('/jobs/applied')) return 'applied-jobs';
    if (path.includes('/candidate/profile')) return 'profile';

    // Roles and Permissions
    if (path.includes('/permissions')) return 'permissions';
    if (path.includes('/permission-sets')) return 'permission-sets';
    if (path.includes('/assign-permissions')) return 'assign-permissions';
    if (path.includes('/roles')) return 'roles';

    // Organization Management
    if (path.includes('/organization-management')) return 'organization-management';
    if (path.includes('/location-management')) return 'location-management';
    if (path.includes('/department-management')) return 'departments-management';
    if (path.includes('/designation-management')) return 'designations-management';
    if (path.includes('/shift-management')) return 'shift-management';
    if (path.includes('/smtp-settings') || path.includes('/smtp/callback')) return 'smtp-settings';
    if (path.includes('/email-providers')) return 'email-providers';

    // Leaves and Holidays
    if (path.includes('/leaves-types')) return 'leave-types';
    if (path.includes('/holidays')) return 'holidays-mangement';

    if (path.includes('/employee/assets-management')) return 'assets-management';
    if (path.includes('/assets-management')) return 'admin-assets-management';
    if (path.includes('/insurance-management')) return 'insurance-management';

    // KPI Management
    if (path.includes('/kpi-dashboard')) return 'kpi-dashboard';
    if (path.includes('/kpi-rules')) return 'kpi-rules';
    if (path.includes('/kpi-slabs')) return 'kpi-slabs';
    if (path.includes('/kpi-entries')) return 'kpi-entries';
    if (path.includes('/kpi-achievements')) return 'kpi-achievements';
    if (path.includes('/kpi-assignments')) return 'kpi-assignments';
    if (path.includes('/kpi-scorecard')) return 'kpi-scorecard';
    if (path.includes('/bonus-management')) return 'bonus-management';
    if (path.includes('/kpis/scorecard')) return 'employee-kpi-scorecard';

    // Overtime Rate Management
    if (path.includes('/overtime-rates')) return 'overtime-rates';

    // Self Evaluation Management
    if (path.includes('/self-evaluations')) return 'self-evaluations';

    // Pulse Survey Management
    if (path.includes('/pulse-surveys') && !path.includes('/recognition-engagement/pulse-surveys')) return 'pulse-surveys';

    // Appraisal Management
    if (path.includes('/hr/appraisals-cycle')) return 'hr-appraisals-cycle';
    if (path.includes('/hr/appraisals-templates')) return 'hr-appraisals-templates';
    if (path.includes('/hr/appraisals')) return 'hr-appraisals';

    if (path.includes('/appraisals-cycle')) return 'appraisals-cycle';
    if (path.includes('/appraisals-templates')) return 'appraisals-templates';
    if (path.includes('/appraisals') && !path.includes('/employee/appraisals')) return 'appraisals';

    // Employee HR/Payroll
    if (path.includes('/generate-payslip')) return 'salary-history';
    if (path.includes('/tax-documents')) return 'tax-documents';
    if (path.includes('/leave-requests')) return 'leave-requests';
    if (path.includes('/attendance') && !path.includes('/attendance-regularization') && !path.includes('/attendance-requests') && !path.includes('/attendance-analytics') && !path.includes('/attendance-rules')) return 'attendance';
    if (path.includes('/self-service/salary-history')) return 'salary-history-self';
    if (path.includes('/salary-history')) return 'salary-history';
    if (path.includes('/overtime-submission')) return 'overtime-submission';
    if (path.includes('/wfh-requests')) return 'wfh-requests';
    if (path.includes('/hr-request')) return 'hr-request';
    if (path.includes('/hr-documents-management')) return 'admin-hr-documents';
    if (path.includes('/hr/documents') || path.includes('/hr-documents')) return isEmployee ? 'employee-hr-documents' : 'hr-documents';
    if (path.includes('/issue-tickets-management')) return 'admin-issue-tickets';
    if (path.includes('/hr/issue-tickets')) return 'hr-issue-tickets';
    if (path.includes('/issue-tickets')) return isEmployee ? 'employee-issue-tickets' : 'issue-tickets';
    if (path.includes('/payroll-deductions-management')) return 'payroll-deductions-management';

    // Employee Self Service
    if (path.includes('/procurement-management/procurement')) return isEmployee ? 'employee-procurement' : 'admin-procurement';
    if (path.includes('/hr-payroll/attendance')) return 'emp-employee-attendance';
    if (path.includes('/pulse-surveys')) return isEmployee ? 'emp-pulse-surveys' : 'pulse-surveys';
    if (path.includes('/self-service/my-documents')) return 'my-documents';
    if (path.includes('/personal-info')) return 'personal-info';
    if (path.includes('/employment-history')) return 'employment-history';
    if (path.includes('/bank-details')) return 'bank-details';
    if (path.includes('/employee/appraisals')) return 'employee-appraisals';
    if (path.includes('/self-evaluation')) return 'self-evaluation';

    if (path.includes('/resignation-management')) return 'resignation-management';
    // Employee Other
    if (path.includes('/hr-policy-documents')) return 'hr-policy-documents';
    if (path.includes('/employee-handbook')) return 'employee-handbook';
    if (path.includes('/training-materials')) return 'training-materials';

    if (path.includes('/kudos-wall')) return 'kudos-wall';
    if (path.includes('/monthly-top-performers')) return 'monthly-top-performers';
    if (path.includes('/pulse-surveys')) return 'pulse-surveys';

    if (path.includes('/resignation-form')) return 'resignation-form';

    if (path.includes('/procurement')) return 'procurement-management';
    if (path.includes('/hr/warning-types')) return 'hr-warning-types';
    if (path.includes('/hr/warning-rules')) return 'hr-warning-rules';
    if (path.includes('/hr/warnings')) return 'hr-warnings';
    if (path.includes('/employee/warnings')) return 'warnings-management';
    if (path.includes('/warning-types')) return 'warning-types-admin';
    if (path.includes('/warning-rules')) return 'warning-rules-admin';
    if (path.includes('/warnings')) return 'warnings-admin';
    if (path.includes('/my-policies')) return 'my-policies';
    if (path.includes('/policies-management') || path.includes('/hr/policies')) return 'policies-admin';
    if (path.includes('/hr-documents-management')) return 'admin-hr-documents';
    if (path.includes('/hr/documents') || path.includes('/hr-documents')) return isEmployee ? 'employee-hr-documents' : 'hr-documents';
    if (path.includes('/exit-workflow')) return 'exit-workflow';
    if (path.includes('/reimbursement-requests')) return 'reimbursement';

    // Attendance Regularization (must come before generic /attendance match)
    if (path.includes('/hr/attendance-regularization')) return 'hr-attendance-regularization';
    if (path.includes('/hr-payroll/attendance-requests/pending/manager')) return 'manager-attendance-requests';
    if (path.includes('/hr-payroll/attendance-regularization')) return 'attendance-regularization';
    if (path.includes('/attendance-regularization')) return 'admin-attendance-regularization';

    // Employee Documents
    if (path.includes('/employee-documents/expiring')) return 'emp-docs-expiring';
    if (path.includes('/employee-documents/missing')) return 'emp-docs-missing';
    if (path.includes('/employee-documents')) return 'emp-docs-all';

    // Salary Revision / Merit — check :id routes first so list doesn't shadow them
    if (path.includes('/hr/merit-matrix')) return isAdmin ? 'admin-merit-matrix' : 'hr-merit-matrix';
    if (path.match(/\/hr\/salary-revisions\/\d+/)) return isAdmin ? 'admin-salary-revisions' : 'hr-salary-revisions';
    if (path.includes('/hr/salary-revisions')) return isAdmin ? 'admin-salary-revisions' : 'hr-salary-revisions';
    // Accountant salary revisions
    if (path.match(/\/salary-revisions\/\d+/)) return 'acc-salary-revisions';
    if (path.includes('/salary-revisions')) return 'acc-salary-revisions';

    // Salary history (employee self-service)
    if (path.includes('/self-service/salary-history')) return 'salary-history-self';

    // Platform admin
    if (path.includes('/admin/subscription')) return 'subscription-page';

    return '';
  };



  return (
    <>
      {/* Mobile backdrop overlay — closes sidebar on click */}
      {isMobile && !collapsed && (
        <div
          onClick={() => onCollapse(true)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.42)',
            zIndex: 998,
            cursor: 'pointer',
          }}
        />
      )}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        width={240}
        collapsedWidth={isMobile ? 0 : 80}
        trigger={null}
        style={{
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E8ECEF',
          boxShadow: isMobile && !collapsed ? '4px 0 24px rgba(0,0,0,0.18)' : '2px 0 8px rgba(0,0,0,0.04)',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(isMobile ? {
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 999,
          } : {}),
        }}
      >
        {/* Logo & Organization Brand Area */}
        <div style={{
          padding: collapsed ? '16px 0 12px' : '16px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderBottom: '1px solid #E8ECEF',
          marginBottom: 8,
          background: '#FFFFFF',
          height: 70,
          justifyContent: 'center',
        }}>
          <Link to="/" style={{
            display: 'flex',
            flexDirection: collapsed ? 'column' : 'row',
            alignItems: 'center',
            gap: collapsed ? 0 : 10,
            textDecoration: 'none',
            width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}>
            {/* Logo */}
            <div style={{
              width: collapsed ? 48 : 56,
              height: collapsed ? 48 : 56,
              flexShrink: 0,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img
                src={logo}
                alt="Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transition: '0.3s',
                }}
              />
            </div>
            {/* Organization Name & Tagline */}
            {!collapsed && (
              <div style={{ textAlign: 'left', overflow: 'hidden', flex: 1 }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#2C3E50',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  letterSpacing: '-0.2px',
                }}>
                  ResourcAI
                </div>
                <div style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#00BAA7',
                  marginTop: 2,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                }}>
                  {isAdmin ? 'Admin Panel' : isRecruiter ? 'Recruitment Suite' : isHR ? 'HR Management' : isEmployee ? 'Employee Portal' : isAccountant ? 'Pay & Benefits' : isCandidate ? 'Candidate Portal' : 'Dashboard'}
                </div>
              </div>
            )}
          </Link>
        </div>

        <div
          ref={menuScrollRef}
          style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            onClick={({ domEvent }) => {
              // On mobile: close sidebar after clicking a menu item
              if (isMobile) onCollapse(true);
              // In collapsed desktop mode: the <Link> inside has zero click area,
              // so navigate programmatically by reading the <a href> from the DOM
              if (collapsed && !isMobile) {
                const li = (domEvent.target as HTMLElement).closest('.ant-menu-item');
                const anchor = li?.querySelector('a');
                if (anchor) {
                  try {
                    const url = new URL(anchor.href);
                    navigate(url.pathname + url.search);
                  } catch { /**/ }
                }
              }
            }}
            style={{
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: '#FFFFFF',
              borderRight: 0,
            }}
          >
            {isPlatform && (
              <ItemGroup key="platform" title={<span style={groupTitleStyle}>PLATFORM</span>}>
                <Menu.Item key="platform-dashboard" icon={<ApartmentOutlined />}>
                  <Link to="/platform/dashboard">Organizations</Link>
                </Menu.Item>
              </ItemGroup>
            )}
            {isAdmin && (
              <>
                <ItemGroup key="admin" title={<span style={groupTitleStyle}>ADMIN Panel</span>}>
                  <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                    <Link to="/v1/admin/dashboard">Dashboard</Link>
                  </Menu.Item>
                  <Menu.Item key="pending-approvals" icon={<AuditOutlined />}>
                    <Link to="/v1/pending-approvals">Pending Approvals</Link>
                  </Menu.Item>
                  <ItemGroup key="managedOrganization">
                    <SubMenu key="organizations-management" icon={<ApartmentOutlined />} title="Organizations">
                      <Menu.Item key="organization-management">
                        <Link to="/v1/organization-management">Organization Management</Link>
                      </Menu.Item>
                      <Menu.Item key="location-management">
                        <Link to="/v1/location-management">Location Management</Link>
                      </Menu.Item>
                      {canAccess({ feature: FeatureCode.ShiftManagement }) ? (
                        <Menu.Item key="shift-management">
                          <Link to="/v1/shift-management">Shifts</Link>
                        </Menu.Item>
                      ) : (
                        <FeatureMenuItem
                          featureCode={FeatureCode.ShiftManagement}
                          label="Shifts"
                          to="/v1/shift-management"
                          menuKey="shift-management"
                        />
                      )}
                      <Menu.Item key="departments-management">
                        <Link to="/v1/department-management">Departments</Link>
                      </Menu.Item>
                      <Menu.Item key="designations-management">
                        <Link to="/v1/designation-management">Designations</Link>
                      </Menu.Item>
                      <Menu.Item key="email-providers">
                        <Link to="/v1/email-providers">Email Providers</Link>
                      </Menu.Item>
                    </SubMenu>
                  </ItemGroup>

                  {canAccess({ module: 'ATTENDANCE' }) ? (
                    <Menu.Item key="attendance-hub" icon={<ClockCircleOutlined />}>
                      <Link to="/attendance">Attendance</Link>
                    </Menu.Item>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="ATTENDANCE"
                      label="Attendance"
                      icon={<ClockCircleOutlined />}
                      menuKey="attendance-hub"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}

                  <SubMenu key="user-management" icon={<TeamOutlined />} title="Employees & Users">
                    <Menu.Item key="all-users">
                      <Link to="/v1/users">Users</Link>
                    </Menu.Item>
                    <Menu.Item key="all-employees">
                      <Link to="/v1/employees">Employees</Link>
                    </Menu.Item>
                    <Menu.Item key="warnings-admin">
                      <Link to="/v1/warnings">Warnings</Link>
                    </Menu.Item>
                    <Menu.Item key="warning-rules-admin">
                      <Link to="/v1/warning-rules">Warning Rules</Link>
                    </Menu.Item>
                    <Menu.Item key="warning-types-admin">
                      <Link to="/v1/warning-types">Warning Types</Link>
                    </Menu.Item>
                    <Menu.Item key="policies-admin">
                      <Link to="/v1/policies-management">Policies</Link>
                    </Menu.Item>
                  </SubMenu>

                  {SHOW_ROLES_PERMISSIONS && (
                    <SubMenu key="permissions-management" icon={<SafetyCertificateOutlined />} title="Roles & Permissions">
                      <Menu.Item key="permissions">
                        <Link to="/v1/permissions">Manage Permissions</Link>
                      </Menu.Item>
                      <Menu.Item key="permission-sets">
                        <Link to="/v1/permission-sets">Permission Sets</Link>
                      </Menu.Item>
                      <Menu.Item key="assign-permissions">
                        <Link to="/v1/assign-permissions">Assign Permissions</Link>
                      </Menu.Item>
                      <Menu.Item key="roles">
                        <Link to="/v1/roles">Roles</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}
                  {appReady && !canAccess({ module: 'PERFORMANCE' }) ? (
                    <Menu.Item
                      key="kpi-management"
                      icon={React.cloneElement(<BarChartOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'PERFORMANCE', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            KPI Management
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <SubMenu key="kpi-management" icon={<BarChartOutlined />} title="KPI Management">
                      <Menu.Item key="kpi-dashboard">
                        <Link to="/v1/kpi-dashboard">KPI Dashboard</Link>
                      </Menu.Item>
                      <Menu.Item key="kpi-rules">
                        <Link to="/v1/kpi-rules">KPI Rules</Link>
                      </Menu.Item>
                      <Menu.Item key="kpi-slabs">
                        <Link to="/v1/kpi-slabs">KPI Slabs</Link>
                      </Menu.Item>
                      <Menu.Item key="kpi-achievements">
                        <Link to="/v1/kpi-achievements">Upload Achievements</Link>
                      </Menu.Item>
                      <Menu.Item key="kpi-assignments">
                        <Link to="/v1/kpi-assignments">KPI Assignments</Link>
                      </Menu.Item>
                      <Menu.Item key="kpi-scorecard">
                        <Link to="/v1/kpi-scorecard">Employee Scorecards</Link>
                      </Menu.Item>
                      <Menu.Item key="bonus-management">
                        <Link to="/v1/bonus-management">Incentive Management</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}

                  {appReady && !canAccess({ module: 'LEAVE' }) ? (
                    <Menu.Item
                      key="leaves-and-holidays"
                      icon={React.cloneElement(<CalendarOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'LEAVE', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Leaves & Holidays
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <Menu.Item key="holidays-mangement" icon={<CalendarOutlined />}>
                      <Link to="/v1/holidays">Holidays</Link>
                    </Menu.Item>
                  )}

                  <SubMenu key="rules-group" title="Rules & Surveys" icon={<ProfileOutlined />}>
                    {canAccess({ feature: FeatureCode.FinalSettlement }) ? (
                      <Menu.Item key="final-settlement-rules-management">
                        <Link to="/v1/final-settlement-rules-management">Final Settlement Rules</Link>
                      </Menu.Item>
                    ) : (
                      <FeatureMenuItem
                        featureCode={FeatureCode.FinalSettlement}
                        label="Final Settlement Rules"
                        to="/v1/final-settlement-rules-management"
                        menuKey="final-settlement-rules-management"
                      />
                    )}

                    <Menu.Item key="insurance-management">
                      <Link to="/v1/insurance-management">Insurance Management</Link>
                    </Menu.Item>

                    {canAccess({ feature: FeatureCode.ProvidentFund }) ? (
                      <Menu.Item key="provident-fund">
                        <Link to="/v1/provident-fund">Provident Fund</Link>
                      </Menu.Item>
                    ) : (
                      <FeatureMenuItem
                        featureCode={FeatureCode.ProvidentFund}
                        label="Provident Fund"
                        to="/v1/provident-fund"
                        menuKey="provident-fund"
                      />
                    )}

                    <Menu.Item key="overtime-rates">
                      <Link to="/v1/overtime-rates">Overtime Rates</Link>
                    </Menu.Item>

                    <Menu.Item key="pulse-surveys">
                      <Link to="/v1/pulse-surveys">Pulse Surveys</Link>
                    </Menu.Item>
                  </SubMenu>
                  <SubMenu key="employee-documents" icon={<FileProtectOutlined />} title="Documents & Assets">
                    <Menu.Item key="admin-hr-documents">
                      <Link to="/v1/hr-documents-management">HR Documents</Link>
                    </Menu.Item>

                    {appReady && !canAccess({ module: 'ASSETS' }) ? (
                      <Menu.Item
                        key="admin-assets-management"
                        onClick={() => openUpgradeModal({ feature: 'ASSETS', currentPlan })}
                        style={{ opacity: 0.5, cursor: 'pointer' }}
                      >
                        <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                          <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              Assets
                            </span>
                            <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                          </span>
                        </Tooltip>
                      </Menu.Item>
                    ) : (
                      <Menu.Item key="admin-assets-management">
                        <Link to="/v1/assets-management">Assets</Link>
                      </Menu.Item>
                    )}

                    <Menu.Item key="emp-docs-all">
                      <Link to="/v1/employee-documents">All Documents</Link>
                    </Menu.Item>
                    <Menu.Item key="emp-docs-expiring">
                      <Link to="/v1/employee-documents/expiring">Expiring Documents</Link>
                    </Menu.Item>
                    <Menu.Item key="emp-docs-missing">
                      <Link to="/v1/employee-documents/missing">Missing Required</Link>
                    </Menu.Item>
                  </SubMenu>

                  {appReady && !canAccess({ module: 'PAYROLL' }) ? (
                    <LockedModuleMenuItem
                      moduleCode="PAYROLL"
                      label="Payroll & Finance"
                      icon={<DollarOutlined />}
                      menuKey="admin-payroll-finance"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  ) : (
                    <SubMenu key="admin-payroll-finance" icon={<DollarOutlined />} title="Payroll & Finance">
                      <Menu.Item key="admin-payroll-management">
                        <Link to="/v1/payroll-management">Payroll</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-payroll-runs">
                        <Link to="/v1/payroll-runs">Payroll Runs</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-payslips-management">
                        <Link to="/v1/payslips-management">Payslips Management</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-payslip-templates">
                        <Link to="/v1/payslip-templates">Payslip Templates</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-payroll-bonus-management">
                        <Link to="/v1/payroll-bonus-management">Incentives</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-bonus-types">
                        <Link to="/v1/bonus-types">Incentive Types</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-deduction-management">
                        <Link to="/v1/deduction-management">Deduction Types</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-payroll-deductions-management">
                        <Link to="/v1/payroll-deductions-management">Deductions</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-tax-management">
                        <Link to="/v1/tax-management">Tax Documents</Link>
                      </Menu.Item>
                      <Menu.Item key="admin-tax-rules">
                        <Link to="/v1/tax-rules">Tax Rules</Link>
                      </Menu.Item>

                      {canAccess({ feature: FeatureCode.Loans }) ? (
                        <Menu.Item key="admin-loans">
                          <Link to="/v1/accountant/loans">Loan Management</Link>
                        </Menu.Item>
                      ) : (
                        <FeatureMenuItem
                          featureCode={FeatureCode.Loans}
                          label="Loan Management"
                          to="/v1/accountant/loans"
                          menuKey="admin-loans"
                        />
                      )}

                      {canAccess({ feature: FeatureCode.SalaryRevision }) ? (
                        <Menu.Item key="admin-salary-revisions">
                          <Link to="/v1/hr/salary-revisions">Salary Revisions</Link>
                        </Menu.Item>
                      ) : (
                        <FeatureMenuItem
                          featureCode={FeatureCode.SalaryRevision}
                          label="Salary Revisions"
                          to="/v1/hr/salary-revisions"
                          menuKey="admin-salary-revisions"
                        />
                      )}

                      {canAccess({ feature: FeatureCode.MeritMatrix }) ? (
                        <Menu.Item key="admin-merit-matrix">
                          <Link to="/v1/hr/merit-matrix">Merit Matrix</Link>
                        </Menu.Item>
                      ) : (
                        <FeatureMenuItem
                          featureCode={FeatureCode.MeritMatrix}
                          label="Merit Matrix"
                          to="/v1/hr/merit-matrix"
                          menuKey="admin-merit-matrix"
                        />
                      )}
                    </SubMenu>
                  )}

                  <SubMenu key="other-group" title="Operations" icon={<AppstoreOutlined />}>
                    {canAccess({ module: 'SUPPORT' }) ? (
                      <Menu.Item key="admin-issue-tickets">
                        <Link to="/v1/issue-tickets-management">Support Tickets</Link>
                      </Menu.Item>
                    ) : (
                      <LockedModuleMenuItem
                        moduleCode="SUPPORT"
                        label="Support Tickets"
                        menuKey="admin-issue-tickets"
                        onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                      />
                    )}

                    <Menu.Item key="admin-procurement">
                      <Link to="/v1/procurement-management/procurement">Procurement</Link>
                    </Menu.Item>
                  </SubMenu>

                  <ItemGroup key="platform-admin" title={<span style={groupTitleStyle}>PLATFORM</span>}>
                    <Menu.Item key="subscription-page" icon={<AppstoreOutlined />}>
                      <Link to="/v1/admin/subscription">Subscription</Link>
                    </Menu.Item>
                  </ItemGroup>
                </ItemGroup>
              </>
            )}

            {isRecruiter && (
              <>
                <ItemGroup key="recruitment" title={<span style={groupTitleStyle}></span>}>
                  <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                    <Link to="/v1/recruiter/dashboard">Dashboard</Link>
                  </Menu.Item>

                  {canAccess({ module: 'RECRUITMENT' }) ? (
                    <>
                      <Menu.Item key="projects" icon={<BuildOutlined />}>
                        <Link to="/v1/projects">Projects</Link>
                      </Menu.Item>

                      <Menu.Item key="jobs" icon={<BuildOutlined />}>
                        <Link to="/v1/jobs">Job Listings</Link>
                      </Menu.Item>

                      <Menu.Item key="all-candidates" icon={<TeamOutlined />}>
                        <Link to="/v1/candidates">All Candidates</Link>
                      </Menu.Item>
                      <Menu.Item key="teams-meetings" icon={<VideoCameraOutlined />}>
                        <Link to="/v1/teams-meetings">Meetings & Interviews</Link>
                      </Menu.Item>

                      <Menu.Item key="rec-onboarding" icon={<UserOutlined />}>
                        <Link to="/v1/recruiter/onboarding">Onboarding</Link>
                      </Menu.Item>
                      <Menu.Item key="rec-onboarding-templates" icon={<FileProtectOutlined />}>
                        <Link to="/v1/recruiter/onboarding-templates">Onboarding Templates</Link>
                      </Menu.Item>
                      <Menu.Item key="rec-offer-templates" icon={<FileTextOutlined />}>
                        <Link to="/v1/recruiter/offer-templates">Offer Letter Templates</Link>
                      </Menu.Item>
                      <Menu.Item key="rec-offboarding" icon={<LogoutOutlined />}>
                        <Link to="/v1/recruiter/offboarding">Offboarding</Link>
                      </Menu.Item>
                      <Menu.Item key="rec-offboarding-templates" icon={<FileProtectOutlined />}>
                        <Link to="/v1/recruiter/offboarding-templates">Offboarding Templates</Link>
                      </Menu.Item>
                    </>
                  ) : (
                    <>
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Projects" icon={<BuildOutlined />} menuKey="projects" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Job Listings" icon={<BuildOutlined />} menuKey="jobs" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="All Candidates" icon={<TeamOutlined />} menuKey="all-candidates" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Meetings & Interviews" icon={<VideoCameraOutlined />} menuKey="teams-meetings" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Onboarding" icon={<UserOutlined />} menuKey="rec-onboarding" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Onboarding Templates" icon={<FileProtectOutlined />} menuKey="rec-onboarding-templates" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Offer Letter Templates" icon={<FileTextOutlined />} menuKey="rec-offer-templates" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Offboarding" icon={<LogoutOutlined />} menuKey="rec-offboarding" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                      <LockedModuleMenuItem moduleCode="RECRUITMENT" label="Offboarding Templates" icon={<FileProtectOutlined />} menuKey="rec-offboarding-templates" onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })} />
                    </>
                  )}
                </ItemGroup>
              </>
            )}

            {isAccountant && (
              <>
                <ItemGroup key="accountant" title={<span style={groupTitleStyle}></span>}>
                  <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                    <Link to="/v1/accountant/dashboard">Dashboard</Link>
                  </Menu.Item>
                  <Menu.Item key="pending-approvals" icon={<AuditOutlined />}>
                    <Link to="/v1/pending-approvals">Pending Approvals</Link>
                  </Menu.Item>
                  {canAccess({ feature: FeatureCode.SalaryRevision }) ? (
                    <SubMenu key="salary-revision-management" icon={<RiseOutlined />} title="Salary Revisions">
                      <Menu.Item key="acc-salary-revisions">
                        <Link to="/v1/salary-revisions">All Revisions</Link>
                      </Menu.Item>
                    </SubMenu>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.SalaryRevision}
                      label="Salary Revisions"
                      icon={<RiseOutlined />}
                      to="/v1/salary-revisions"
                      menuKey="salary-revision-management"
                    />
                  )}

                  {appReady && !canAccess({ module: 'PAYROLL' }) ? (
                    <Menu.Item
                      key="payroll"
                      icon={React.cloneElement(<DollarOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'PAYROLL', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Payroll
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <SubMenu key="payroll" icon={<DollarOutlined />} title="Payroll">
                      <Menu.Item key="payroll-management">
                        <Link to="/v1/payroll-management">Payroll</Link>
                      </Menu.Item>
                      <Menu.Item key="payroll-runs">
                        <Link to="/v1/payroll-runs">Payroll Runs</Link>
                      </Menu.Item>
                      <Menu.Item key="payslips-management">
                        <Link to="/v1/payslips-management">Payslips Management</Link>
                      </Menu.Item>
                      <Menu.Item key="payslip-templates">
                        <Link to="/v1/payslip-templates">Payslip Templates</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}

                  {appReady && !canAccess({ module: 'PAYROLL' }) ? (
                    <Menu.Item
                      key="bonus"
                      icon={React.cloneElement(<TrophyOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'PAYROLL', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Incentives
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <SubMenu key="bonus" icon={<TrophyOutlined />} title="Incentives">
                      <Menu.Item key="payroll-bonus-management">
                        <Link to="/v1/payroll-bonus-management">Incentives</Link>
                      </Menu.Item>
                      <Menu.Item key="bonus-types">
                        <Link to="/v1/bonus-types">Incentive Types</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}

                  {appReady && !canAccess({ module: 'PAYROLL' }) ? (
                    <Menu.Item
                      key="deduction"
                      icon={React.cloneElement(<MinusCircleOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'PAYROLL', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Deduction
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <SubMenu key="deduction" icon={<MinusCircleOutlined />} title="Deduction">
                      <Menu.Item key="deduction-management">
                        <Link to="/v1/deduction-management">Deduction Types</Link>
                      </Menu.Item>
                      <Menu.Item key="payroll-deductions-management">
                        <Link to="/v1/payroll-deductions-management">Deductions</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}

                  {canAccess({ feature: FeatureCode.Loans }) ? (
                    <Menu.Item key="accountant-loans" icon={<BankOutlined />}>
                      <Link to="/v1/accountant/loans">Loan Management</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.Loans}
                      label="Loan Management"
                      icon={<BankOutlined />}
                      to="/v1/accountant/loans"
                      menuKey="accountant-loans"
                    />
                  )}

                  {appReady && !canAccess({ module: 'PAYROLL' }) ? (
                    <Menu.Item
                      key="tax"
                      icon={React.cloneElement(<FileProtectOutlined /> as any, { style: { opacity: 0.45 } })}
                      onClick={() => openUpgradeModal({ feature: 'PAYROLL', currentPlan })}
                      style={{ opacity: 0.5, cursor: 'pointer' }}
                    >
                      <Tooltip title="Upgrade your plan to unlock this feature" placement="right">
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%', gap: 6, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Tax
                          </span>
                          <LockOutlined style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }} />
                        </span>
                      </Tooltip>
                    </Menu.Item>
                  ) : (
                    <SubMenu key="tax" icon={<FileProtectOutlined />} title="Tax">
                      <Menu.Item key="tax-management">
                        <Link to="/v1/tax-management">Tax Documents</Link>
                      </Menu.Item>
                      <Menu.Item key="tax-rules">
                        <Link to="/v1/tax-rules">Tax Rules</Link>
                      </Menu.Item>
                    </SubMenu>
                  )}

                  <Menu.Item key="employees-management" icon={<TeamOutlined />}>
                    <Link to="/v1/employees-management">Employees</Link>
                  </Menu.Item>
                  {canAccess({ module: 'ATTENDANCE' }) ? (
                    <Menu.Item key="attendance-hub" icon={<ClockCircleOutlined />}>
                      <Link to="/attendance">Attendance</Link>
                    </Menu.Item>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="ATTENDANCE"
                      label="Attendance"
                      icon={<ClockCircleOutlined />}
                      menuKey="attendance-hub"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}
                  {canAccess({ feature: FeatureCode.FinalSettlement }) ? (
                    <Menu.Item key="approval-resignation" icon={<DollarOutlined />}>
                      <Link to="/v1/resignations">Settlement Queue</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.FinalSettlement}
                      label="Settlement Queue"
                      icon={<DollarOutlined />}
                      to="/v1/resignations"
                      menuKey="approval-resignation"
                    />
                  )}
                  {canAccess({ feature: FeatureCode.FinalSettlement }) ? (
                    <Menu.Item key="final-settlement" icon={<CheckCircleOutlined />}>
                      <Link to="/v1/final-settlement">Settlement Evaluations</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.FinalSettlement}
                      label="Settlement Evaluations"
                      icon={<CheckCircleOutlined />}
                      to="/v1/final-settlement"
                      menuKey="final-settlement"
                    />
                  )}
                  {canAccess({ feature: FeatureCode.Reimbursements }) ? (
                    <Menu.Item key="reimbursement-management" icon={<DollarOutlined />}>
                      <Link to="/v1/reimbursement-management">Reimbursements</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.Reimbursements}
                      label="Reimbursements"
                      icon={<DollarOutlined />}
                      to="/v1/reimbursement-management"
                      menuKey="reimbursement-management"
                    />
                  )}
                  {canAccess({ feature: FeatureCode.ProvidentFund }) ? (
                    <Menu.Item key="provident-fund" icon={<BankOutlined />}>
                      <Link to="/v1/provident-fund">Provident Fund</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.ProvidentFund}
                      label="Provident Fund"
                      icon={<BankOutlined />}
                      to="/v1/provident-fund"
                      menuKey="provident-fund"
                    />
                  )}

                </ItemGroup>
              </>
            )}

            {isCandidate && (
              <ItemGroup key="candidatePanel" title={<span style={groupTitleStyle}>CANDIDATE PANEL</span>}>
                <Menu.Item key="candidate-dashboard" icon={<DashboardOutlined />}>
                  <Link to="/v1/candidate/dashboard">My Dashboard</Link>
                </Menu.Item>

                <Menu.Item key="available-jobs" icon={<BuildOutlined />}>
                  <Link to="/v1/jobs/available">Available Jobs</Link>
                </Menu.Item>

                <Menu.Item key="applied-jobs" icon={<SolutionOutlined />}>
                  <Link to="/v1/jobs/applied">My Applications</Link>
                </Menu.Item>
              </ItemGroup>
            )}

            {isHR && (
              <>
                <ItemGroup key="hr" title={<span style={groupTitleStyle}></span>}>
                  <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                    <Link to="/v1/hr/dashboard">Dashboard</Link>
                  </Menu.Item>
                  <Menu.Item key="pending-approvals" icon={<AuditOutlined />}>
                    <Link to="/v1/pending-approvals">Pending Approvals</Link>
                  </Menu.Item>
                  <SubMenu key="people-operations-group" title="People Operations" icon={<TeamOutlined />}>
                    <Menu.Item key="hr-requests">
                      <Link to="/v1/hr/requests">HR Requests</Link>
                    </Menu.Item>
                    <Menu.Item key="hr-documents">
                      <Link to="/v1/hr/documents">HR Documents</Link>
                    </Menu.Item>
                    <Menu.Item key="hr-employees">
                      <Link to="/v1/hr/employees">Employees</Link>
                    </Menu.Item>
                    {canAccess({ module: 'ATTENDANCE' }) ? (
                      <Menu.Item key="attendance-hub">
                        <Link to="/attendance">Attendance</Link>
                      </Menu.Item>
                    ) : (
                      <LockedModuleMenuItem
                        moduleCode="ATTENDANCE"
                        label="Attendance"
                        menuKey="attendance-hub"
                        onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                      />
                    )}
                    <Menu.Item key="hr-team-resignations">
                      <Link to="/v1/hr/team-resignations">Exit Management</Link>
                    </Menu.Item>
                  </SubMenu>

                  <SubMenu key="hr-management-group" title="Management & Finance" icon={<BankOutlined />}>
                    {canAccess({ module: 'PAYROLL' }) ? (
                      <Menu.Item key="hr-salary-structure">
                        <Link to="/v1/hr/salary-structure">Salary Structure</Link>
                      </Menu.Item>
                    ) : (
                      <LockedModuleMenuItem
                        moduleCode="PAYROLL"
                        label="Salary Structure"
                        icon={<DollarOutlined />}
                        menuKey="hr-salary-structure"
                        onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                      />
                    )}
                    {canAccess({ module: 'SUPPORT' }) ? (
                      <Menu.Item key="hr-issue-tickets">
                        <Link to="/v1/hr/issue-tickets">Support Tickets</Link>
                      </Menu.Item>
                    ) : (
                      <LockedModuleMenuItem
                        moduleCode="SUPPORT"
                        label="Support Tickets"
                        menuKey="hr-issue-tickets"
                        onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                      />
                    )}

                    <Menu.Item key="hr-warnings">
                      <Link to="/v1/hr/warnings">Warnings</Link>
                    </Menu.Item>

                    <Menu.Item key="hr-warning-rules">
                      <Link to="/v1/hr/warning-rules">Warning Rules</Link>
                    </Menu.Item>

                    <Menu.Item key="hr-warning-types">
                      <Link to="/v1/hr/warning-types">Warning Types</Link>
                    </Menu.Item>

                    {canAccess({ feature: FeatureCode.Loans }) ? (
                      <>
                        <Menu.Item key="hr-loan-types">
                          <Link to="/v1/hr/loan-types">Loan Types</Link>
                        </Menu.Item>
                        <Menu.Item key="hr-loans">
                          <Link to="/v1/hr/loans">All Loans</Link>
                        </Menu.Item>
                      </>
                    ) : (
                      <FeatureMenuItem
                        featureCode={FeatureCode.Loans}
                        label="Loan Management"
                        to="/v1/hr/loans"
                        menuKey="hr-loans"
                      />
                    )}
                  </SubMenu>

                  <SubMenu key="employee-documents" icon={<FileProtectOutlined />} title="Employee Documents">
                    <Menu.Item key="emp-docs-all">
                      <Link to="/v1/hr/employee-documents">All Documents</Link>
                    </Menu.Item>
                    <Menu.Item key="emp-docs-expiring">
                      <Link to="/v1/hr/employee-documents/expiring">Expiring Documents</Link>
                    </Menu.Item>
                    <Menu.Item key="emp-docs-missing">
                      <Link to="/v1/hr/employee-documents/missing">Missing Required</Link>
                    </Menu.Item>
                  </SubMenu>

                  <SubMenu key="hr-appraisal-revision-group" title="Appraisals & Revisions" icon={<ProfileOutlined />}>
                    {canAccess({ module: 'PERFORMANCE' }) ? (
                      <>
                        <Menu.Item key="hr-appraisals">
                          <Link to="/v1/hr/appraisals">Appraisals</Link>
                        </Menu.Item>
                        <Menu.Item key="hr-pending-appraisals">
                          <Link to="/v1/hr/appraisals/pending/hr">Pending Sign-Offs (HR)</Link>
                        </Menu.Item>
                        <Menu.Item key="hr-appraisals-cycle">
                          <Link to="/v1/hr/appraisals-cycle">Appraisals Cycle</Link>
                        </Menu.Item>
                        <Menu.Item key="hr-appraisals-templates">
                          <Link to="/v1/hr/appraisals-templates">Appraisals Templates</Link>
                        </Menu.Item>
                      </>
                    ) : (
                      <LockedModuleMenuItem
                        moduleCode="PERFORMANCE"
                        label="Appraisals"
                        menuKey="hr-appraisals"
                        onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                      />
                    )}

                    {canAccess({ feature: FeatureCode.SalaryRevision }) ? (
                      <>
                        <Menu.Item key="hr-salary-revisions">
                          <Link to="/v1/hr/salary-revisions">All Revisions</Link>
                        </Menu.Item>
                        {canAccess({ feature: FeatureCode.MeritMatrix }) ? (
                          <Menu.Item key="hr-merit-matrix">
                            <Link to="/v1/hr/merit-matrix">Merit Matrix</Link>
                          </Menu.Item>
                        ) : (
                          <FeatureMenuItem
                            featureCode={FeatureCode.MeritMatrix}
                            label="Merit Matrix"
                            to="/v1/hr/merit-matrix"
                            menuKey="hr-merit-matrix"
                          />
                        )}
                      </>
                    ) : (
                      <FeatureMenuItem
                        featureCode={FeatureCode.SalaryRevision}
                        label="Salary Revisions"
                        to="/v1/hr/salary-revisions"
                        menuKey="hr-salary-revisions"
                      />
                    )}
                  </SubMenu>
                  <Menu.Item key="hr-feed" icon={<AppstoreAddOutlined />}>
                    <Link to="/v1/hr/feed">Company Feed</Link>
                  </Menu.Item>
                </ItemGroup>
              </>
            )}


            {isEmployee && (
              <ItemGroup key="employeePanel" title={<span style={groupTitleStyle}></span>}>
                <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                  <Link to="/v1/employee/dashboard">Dashboard</Link>
                </Menu.Item>

                <SubMenu key="self-service" title="Self Service" icon={<UserOutlined />}>
                  <Menu.Item key="personal-info">
                    <Link to="/v1/self-service/personal-info">Personal Information</Link>
                  </Menu.Item>
                  <Menu.Item key="employment-history">
                    <Link to="/v1/self-service/employment-history">Employment History</Link>
                  </Menu.Item>
                  <Menu.Item key="bank-details">
                    <Link to="/v1/self-service/bank-details">Bank Details</Link>
                  </Menu.Item>
                  {canAccess({ module: 'ASSETS' }) ? (
                    <Menu.Item key="assets-management">
                      <Link to="/v1/employee/assets-management">My Assets</Link>
                    </Menu.Item>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="ASSETS"
                      label="My Assets"
                      menuKey="assets-management"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}
                  <Menu.Item key="my-documents">
                    <Link to="/v1/self-service/my-documents">My Documents</Link>
                  </Menu.Item>
                  {canAccess({ module: 'PAYROLL' }) ? (
                    <Menu.Item key="salary-history-self">
                      <Link to="/v1/self-service/salary-history">Salary History</Link>
                    </Menu.Item>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="PAYROLL"
                      label="Salary History"
                      menuKey="salary-history-self"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}
                </SubMenu>

                {canAccess({ module: 'ATTENDANCE' }) ? (
                  <SubMenu key="attendance" title="Attendance" icon={<ClockCircleOutlined />}>
                    <Menu.Item key="emp-employee-attendance">
                      <Link to="/v1/hr-payroll/attendance">My Attendance</Link>
                    </Menu.Item>
                  </SubMenu>
                ) : (
                  <LockedModuleMenuItem
                    moduleCode="ATTENDANCE"
                    label="Attendance"
                    icon={<ClockCircleOutlined />}
                    menuKey="attendance"
                    onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                  />
                )}

                <SubMenu key="payroll" title="Payroll" icon={<DollarOutlined />}>
                  {canAccess({ module: 'PAYROLL' }) ? (
                    <>
                      <Menu.Item key="salary-history">
                        <Link to="/v1/hr-payroll/generate-payslip">Payslips</Link>
                      </Menu.Item>
                      <Menu.Item key="tax-documents">
                        <Link to="/v1/hr-payroll/tax-documents">Tax Documents</Link>
                      </Menu.Item>
                    </>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="PAYROLL"
                      label="Payroll"
                      menuKey="salary-history"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}
                  {canAccess({ feature: FeatureCode.Loans }) ? (
                    <Menu.Item key="my-loans">
                      <Link to="/v1/hr-payroll/my-loans">My Loans</Link>
                    </Menu.Item>
                  ) : (
                    <FeatureMenuItem
                      featureCode={FeatureCode.Loans}
                      label="My Loans"
                      to="/v1/hr-payroll/my-loans"
                      menuKey="my-loans"
                    />
                  )}
                </SubMenu>

                <SubMenu key="employee-recognition" title="Recognition & Appraisals" icon={<TrophyOutlined />}>
                  {canAccess({ module: 'PERFORMANCE' }) ? (
                    <>
                      <Menu.Item key="employee-appraisals">
                        <Link to="/v1/employee/appraisals">My Appraisals</Link>
                      </Menu.Item>
                      <Menu.Item key="employee-kpi-scorecard">
                        <Link to="/v1/kpis/scorecard">My KPI Scorecard</Link>
                      </Menu.Item>
                    </>
                  ) : (
                    <LockedModuleMenuItem
                      moduleCode="PERFORMANCE"
                      label="Appraisals"
                      menuKey="employee-appraisals"
                      onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                    />
                  )}
                  <Menu.Item key="kudos-wall">
                    <Link to="/v1/recognition-engagement/kudos-wall">Kudos Wall</Link>
                  </Menu.Item>
                  <Menu.Item key="emp-pulse-surveys">
                    <Link to="/v1/recognition-engagement/pulse-surveys">Pulse Surveys</Link>
                  </Menu.Item>
                </SubMenu>

                <SubMenu key="employee-hr-requests" title="HR Requests" icon={<SolutionOutlined />}>
                  <Menu.Item key="hr-request">
                    <Link to="/v1/hr-request">HR Request</Link>
                  </Menu.Item>
                  <Menu.Item key="employee-hr-documents">
                    <Link to="/v1/hr-documents">HR Documents</Link>
                  </Menu.Item>
                </SubMenu>

                <Menu.Item key="feed" icon={<AppstoreOutlined />}>
                  <Link to="/v1/feed">Feeds</Link>
                </Menu.Item>
                {canAccess({ module: 'SUPPORT' }) ? (
                  <Menu.Item key="employee-issue-tickets" icon={<ProfileOutlined />}>
                    <Link to="/v1/issue-tickets">Support Tickets</Link>
                  </Menu.Item>
                ) : (
                  <LockedModuleMenuItem
                    moduleCode="SUPPORT"
                    label="Support Tickets"
                    icon={<ProfileOutlined />}
                    menuKey="employee-issue-tickets"
                    onLockedClick={(m) => openUpgradeModal({ feature: m, currentPlan })}
                  />
                )}
                <Menu.Item key="resignation-form" icon={<FileTextOutlined />}>
                  <Link to="/v1/resignation/resignation-form">Resignation Form</Link>
                </Menu.Item>
                <Menu.Item key="employee-procurement" icon={<CalculatorOutlined />}>
                  <Link to="/v1/procurement-management/procurement">Procurement</Link>
                </Menu.Item>
                <Menu.Item key="warnings-management" icon={<WarningOutlined />}>
                  <Link to="/v1/employee/warnings">My Warnings</Link>
                </Menu.Item>
                <Menu.Item key="my-policies" icon={<SafetyCertificateOutlined />}>
                  <Link to="/v1/my-policies">My Policies</Link>
                </Menu.Item>
                {canAccess({ feature: FeatureCode.Reimbursements }) ? (
                  <Menu.Item key="reimbursement" icon={<DollarOutlined />}>
                    <Link to="/v1/reimbursement-requests">Reimbursement Requests</Link>
                  </Menu.Item>
                ) : (
                  <FeatureMenuItem
                    featureCode={FeatureCode.Reimbursements}
                    label="Reimbursement Requests"
                    icon={<DollarOutlined />}
                    to="/v1/reimbursement-requests"
                    menuKey="reimbursement"
                  />
                )}

                {isManager && (
                  <ItemGroup key="managedHrPayrollEmployee" title={<span style={groupTitleStyle}>MANAGED HR & PAYROLL</span>}>
                    <SubMenu key="leave-attendance-managed" icon={<FileTextOutlined />} title="Team Approvals">
                      <Menu.Item key="resignation-management">
                        <Link to="/v1/resignation-management">Resignation Approvals</Link>
                      </Menu.Item>
                      <Menu.Item key="manager-pending-appraisals">
                        <Link to="/v1/employee/appraisals/pending/manager">Pending Appraisals</Link>
                      </Menu.Item>
                    </SubMenu>
                  </ItemGroup>
                )}
              </ItemGroup>
            )}

            {/* Optionally show some shared items for all roles if needed */}
          </Menu>
        </div>
      </Sider>
    </>
  );
};

const groupTitleStyle: React.CSSProperties = {
  color: '#95A5A6',
  fontSize: 11,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  fontWeight: 600,
  paddingLeft: 8,
};

export default SidebarMenu;